import Backbone from 'backbone';
import { View } from 'backbone.marionette';
import Syphon from 'backbone.syphon';
import _ from 'lodash';

import ButtonCell from '@/backgrid/ButtonCell';
import GridView from '@/views/GridView';
import PageableCollection from '@/backbone/PageableCollection';
import StringCell from '@/backgrid/StringCell';
import formatAsDollars from '@/util/formatAsDollars';
import showModal from '@/util/showModal';
import showErrorMessage from '@/util/showErrorMessage';
import showSuccessMessage from '@/util/ShowSuccessMessage';
import { validateForm } from '@/util/ParsleyUtils';

import ReloadViewModule from '../../../services/ReloadViewModule';
import SaveCustomerOrderDevices from '../../../services/SaveCustomerOrderDevices';
import SpecialtyRequirementDeviceEditModalView from '../../../views/SpecialtyRequirementDeviceEditModalView';
import GetAllOrderFormDataByOrderId from '../../../services/GetAllOrderFormDataByOrderId';

import './SpecialtyRequirementFormView.css';

import tmplSpecialtyRequirementFormView from './SpecialtyRequirementFormView.hbs';

const SpecialtyRequirementFormView = View.extend({
  template: tmplSpecialtyRequirementFormView,

  className: 'specialty-requirement-form-view',

  ui: {
    customDescription: '#customDescription',
    deviceQuantity: '#deviceQuantity',
    devicePrice: '#devicePrice',
    devicesAddSection: '.devices-add-section',
    devicesGridContainer: '.devices-grid-container',
    btnAdd: '.btn-add',
    btnReset: '.btn-reset',
    quantityAlert: '.quantity-error',
    priceAlert: '.price-error',
    noDevicesAlert: '.no-devices-error',
    descriptionSection: '.description-section'
  },

  regions: {
    devicesAddSection: '@ui.devicesAddSection',
    devicesGridContainer: '@ui.devicesGridContainer',
    descriptionSection: '@ui.descriptionSection'
  },

  events: {
    'click @ui.btnAdd': 'addSpecialtyRequirementDevice',
    'click @ui.btnReset': 'onClickReset'
  },

  onRender() {
    const self = this;
    const isReadOnly = this.model.get('orderDetails').isReadOnly;

    const statusId = this.model.get('orderDetails').statusId;
    const isPriceVisible = this.model.get('isPriceVisible');

    this.devicesGridContainer = new GridView({
      columns: [
        {
          name: 'deleteSpecialtyRequirementDevice',
          label: '',
          ariaLabel: 'Delete Device',
          sortable: false,
          headerCell: 'custom',
          cell: ButtonCell.extend({
            labelClass: 'fa fa-minus',
            buttonClass: 'btn-sm delete-specialty-requirement-device',
            onClick: this.onClickDeleteRow.bind(this)
          }),
          width: 10
        },
        {
          name: 'customDescription',
          label: 'Description',
          cell: StringCell,
          headerCell: 'custom',
          width: 50,
          editable: false,
          sortable: true
        },
        {
          name: 'quantity',
          label: 'Quantity',
          cell: StringCell,
          headerCell: 'custom',
          width: 20,
          editable: false,
          sortable: true
        },
        {
          name: 'formattedPrice',
          label: 'Unit Price',
          cell: StringCell,
          headerCell: 'custom',
          width: 10,
          renderable: isPriceVisible,
          editable: false,
          sortable: true
        },
        {
          name: 'editDevice',
          label: '',
          editable: false,
          sortable: false,
          width: 10,
          cell: ButtonCell.extend({
            buttonClass: 'btn btn-sm btn-outline-primary text-center',
            name() {
              if (isReadOnly) {
                return 'View';
              }
              return 'Edit';
            },
            onClick(e, model) {
              isReadOnly ? self.onClickView(e, model, isReadOnly) : self.onClickEdit(e, model);
            }
          })
        }
      ],
      collection: new PageableCollection(this.model.get('orderItems'), { pageSize: 25 }),
      emptyText: 'No Devices'
    });
    this.showChildView('devicesGridContainer', this.devicesGridContainer);

    // this.showChildView(
    //   'descriptionSection',
    //   new OrderDeviceSectionView({
    //     model: new Backbone.Model({
    //       title: 'Device Information',
    //       type: 'description',
    //       id: 'description',
    //       contract: 'SpecialtyRequirement'
    //     })
    //   })
    // );
  },

  saveRowItem(modalData, model) {
    model.set('quantity', modalData.quantity);
    model.set('customDescription', modalData.customDescription);

    if (modalData.price) {
      model.set('price', modalData.price);
      model.set('formattedPrice', formatAsDollars(modalData.price));
    }
  },

  async onClickEdit(e, model) {
    const isPriceVisible = this.model.get('isPriceEditable');

    e.preventDefault();
    const device = model.attributes;
    const modalData = await showModal({
      title: 'Edit Device Information',
      view: new SpecialtyRequirementDeviceEditModalView({
        model: new Backbone.Model({ device, isPriceVisible })
      }),
      dialogClasses: 'modal-lg'
    });
    this.saveRowItem(modalData, model);
  },

  async onClickView(e, model, isReadOnly) {
    const isPriceVisible = this.model.get('isPriceVisible');

    e.preventDefault();
    const device = model.attributes;
    await showModal({
      title: 'View Device Information',
      view: new SpecialtyRequirementDeviceEditModalView({
        model: new Backbone.Model({ device, isReadOnly, isPriceVisible })
      }),
      dialogClasses: 'modal-lg'
    });
  },

  async addSpecialtyRequirementDevice(e) {
    e.preventDefault();
    const formData = Syphon.serialize(this.ui.devicesAddSection);
    try {
      await validateForm(this.ui.devicesAddSection);
      this.devicesGridContainer.collection.add({
        quantity: formData.deviceQuantity,
        customDescription: formData.customDescription,
        price: formData.devicePrice
      });
    } catch (err) {
      // ignore
    }
  },

  onClickReset(e) {
    e.preventDefault();
    this.ui.customDescription.val('');
    this.ui.deviceQuantity.val('');
    this.ui.devicePrice.val('');
  },

  onClickDeleteRow(e, model) {
    e.preventDefault();
    this.devicesGridContainer.collection.remove(model);
  },

  showSaveError() {
    showErrorMessage({
      errorCode: 'saveError',
      errorTitle: 'Save Error',
      errorMessage:
        'An error occurred while saving devices for this order. Try again or contact an administrator if your issue persists.'
    });
  },

  showSaveSuccess() {
    showSuccessMessage({
      code: 'saveSuccess',
      title: 'Save Success',
      message: 'This order has been saved successfully.',
      hideAfter: 5
    });
  },

  async refreshGridIds() {
    const orderId = this.model.get('orderDetails')?.orderId;
    const data = await GetAllOrderFormDataByOrderId(orderId);
    _.each(this.devicesGridContainer.collection.models, (model, i) => {
      if (!model.get('id')) model.set('id', data[i]?.id);
    });
  },

  validate(calledOnSubmit) {
    const isPriceEditable = this.model.get('isPriceVisible') && !EXTERNAL;
    const devicesFormData = this.devicesGridContainer.collection.toJSON();

    let isValid = true;
    if (!devicesFormData.length) {
      this.ui.noDevicesAlert.show();
      this.trigger('has:errors');
      return false;
    }
    _.each(devicesFormData, (item) => {
      if (!item.quantity || item?.quantity === '0') {
        isValid = false;
        this.ui.quantityAlert.show();
      }

      const validateUnitPrice =
        (calledOnSubmit || this.model.get('orderDetails').orderDetails.statusId !== '3') &&
        isPriceEditable &&
        (!item.price || !parseFloat(item?.price));

      if (validateUnitPrice) {
        isValid = false;
        this.ui.priceAlert.show();
      }
    });
    isValid ? this.trigger('no:errors') : this.trigger('has:errors');
    return isValid;
  },

  hideErrors() {
    this.ui.quantityAlert.hide();
    this.ui.noDevicesAlert.hide();
    this.ui.priceAlert.hide();
  },

  async onSave() {
    this.hideErrors();

    const devicesFormData = this.devicesGridContainer.collection.toJSON();

    if (!this.validate()) return;

    // Set CLINs - pad start with 0s
    _.each(devicesFormData, (item, i) => {
      item.clin = _.padStart(parseInt(i + 1), 4, '0');
      return item;
    });

    const formData = {
      devicesFormData,
      orderId: this.model.get('orderDetails').orderId,
      orderTypeId: this.model.get('orderDetails').orderTypeId,
      itPocExists: true
    };

    try {
      await SaveCustomerOrderDevices(formData);
      await this.refreshGridIds();
      await ReloadViewModule.reload();
      this.showSaveSuccess();
    } catch (err) {
      this.showSaveError();
      throw new Error(err);
    }
  }
});

export default SpecialtyRequirementFormView;
